/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronDown, Building2 } from 'lucide-react';
import { useSelectedUser } from './staff/SelectedUserProvider';

const MenubarCompanyBadge = ({ name }) => (
    <div className="hms-menubar-company" title={name}>
        <Building2 size={16} className="hms-menubar-company-icon" aria-hidden="true" />
        <span className="hms-menubar-company-name">{name}</span>
    </div>
);

const ONBOARDING_KEY = 'onboarding';
const ONBOARDING_ITEMS = [
    { link: 'onboarding', name: 'Hospital List' },
    { link: 'bcsetup', name: 'BC Setup' },
    { link: 'apisetup', name: 'API Setup' },
    { link: 'insurance-list', name: 'Insurance Company List' },
    { link: 'data-upload', name: 'Data Upload' },
    { link: 'data-migration', name: 'Data Migration' },
];

const formatMenuLabel = (name = '') =>
    String(name).replaceAll('_', ' ');

const isPathActive = (activePath, link) =>
    activePath === link || activePath.startsWith(`${link}/`);

const MenuDropdownPanel = ({ items, activeClassName, position, onClose }) => {
    if (!position) {
        return null;
    }

    return createPortal(
        <ul
            className="hms-menubar-menu hms-menubar-menu-portal"
            role="menu"
            style={{
                top: position.top,
                left: position.left,
                minWidth: position.minWidth,
            }}
        >
            {items.map((item) => (
                <li key={item.orderId || item.link} role="none">
                    <Link
                        role="menuitem"
                        className={`hms-menubar-menu-item${activeClassName === item.link ? ' is-active' : ''}`}
                        to={item.to}
                        onClick={onClose}
                    >
                        {item.name}
                    </Link>
                </li>
            ))}
        </ul>,
        document.body
    );
};

const AppMenubar = ({ activeClassName = '' }) => {
    const { menuList, loginRole, loginFeatures: features, hospital } = useSelectedUser();
    const [selected, setSelected] = useState({});
    const [isReady, setIsReady] = useState(false);
    const [openKey, setOpenKey] = useState(null);
    const [menuPosition, setMenuPosition] = useState(null);
    const menubarRef = useRef(null);
    const triggerRefs = useRef({});

    useEffect(() => {
        if (!features) {
            return;
        }

        const assigned = {};
        Object.keys(features).forEach((key) => {
            const fdata = Array.isArray(features[key]) ? features[key] : [];
            assigned[key] = fdata.map((item) => item.menuId);
        });

        setSelected(assigned);
        setIsReady(true);
    }, [features]);

    useEffect(() => {
        setOpenKey(null);
        setMenuPosition(null);
    }, [activeClassName]);

    const updateMenuPosition = useCallback((key) => {
        const trigger = triggerRefs.current[key];
        if (!trigger) {
            return;
        }

        const rect = trigger.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + 4,
            left: rect.left,
            minWidth: Math.max(rect.width, 224),
        });
    }, []);

    useEffect(() => {
        if (!openKey) {
            setMenuPosition(null);
            return undefined;
        }

        updateMenuPosition(openKey);

        const handleReposition = () => updateMenuPosition(openKey);
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);

        return () => {
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [openKey, updateMenuPosition]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const inMenubar = menubarRef.current?.contains(event.target);
            const inPortalMenu = event.target.closest('.hms-menubar-menu-portal');

            if (!inMenubar && !inPortalMenu) {
                setOpenKey(null);
                setMenuPosition(null);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setOpenKey(null);
                setMenuPosition(null);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const mainMenus = menuList?.mainMenus || menuList?.mainMenu || {};

    const menuGroups = useMemo(() => {
        if (!isReady || !mainMenus || Object.keys(mainMenus).length === 0) {
            return { singles: [], groups: [] };
        }

        const singles = [];
        const groups = [];

        Object.keys(mainMenus).forEach((key) => {
            if (!menuList.menus?.[key] || selected[key] === undefined) {
                return;
            }

            const mainMenu = mainMenus[key][0];
            const label = formatMenuLabel(mainMenu.name);
            const children = menuList.menus[key]
                .filter((m) => selected[key].includes(m.orderId) || selected[key].includes(m.menuId))
                .map((m) => ({ ...m, to: `/${m.link || mainMenu.link}` }));

            // Order keys 1–2 are top-level singles (Dashboard, Flowboard).
            if ([1, 2].includes(parseInt(key, 10))) {
                singles.push({
                    key,
                    label,
                    to: `/${mainMenu.link}`,
                    paths: [mainMenu.link, ...children.map((c) => c.link || mainMenu.link)],
                });
                return;
            }

            if (children.length === 0) {
                return;
            }

            groups.push({
                key,
                label,
                items: children,
                paths: children.map((c) => c.link || mainMenu.link),
            });
        });

        return { singles, groups };
    }, [isReady, menuList, mainMenus, selected]);

    const isGroupActive = (paths) =>
        paths.some((path) => isPathActive(activeClassName, path));

    const closeMenu = () => {
        setOpenKey(null);
        setMenuPosition(null);
    };

    const toggleGroup = (key) => {
        setOpenKey((current) => {
            if (current === key) {
                setMenuPosition(null);
                return null;
            }
            return key;
        });
    };

    const openMenuItems = useMemo(() => {
        if (!openKey) {
            return null;
        }

        if (openKey === ONBOARDING_KEY) {
            return ONBOARDING_ITEMS.map((item) => ({
                ...item,
                orderId: item.link,
                to: `/${item.link}`,
            }));
        }

        const group = menuGroups.groups.find((g) => g.key === openKey);
        return group?.items || null;
    }, [openKey, menuGroups.groups]);

    const companyName = hospital?.name || 'Hospital';

    if (!isReady) {
        return (
            <div className="hms-menubar">
                <div className="hms-menubar-scroll">
                    <nav className="hms-menubar-nav" aria-label="Main navigation">
                        <MenubarCompanyBadge name={companyName} />
                        <div className="hms-menubar-loading">Loading menu…</div>
                    </nav>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="hms-menubar" ref={menubarRef}>
                <div className="hms-menubar-scroll">
                    <nav className="hms-menubar-nav" aria-label="Main navigation">
                        <MenubarCompanyBadge name={companyName} />

                        {menuGroups.singles.map((item) => {
                            const active = isGroupActive(item.paths);
                            return (
                                <Link
                                    key={item.key}
                                    to={item.to}
                                    className={`hms-menubar-link${active ? ' is-active' : ''}`}
                                    onClick={closeMenu}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}

                        {menuGroups.groups.map((group) => {
                            const active = isGroupActive(group.paths);
                            const isOpen = openKey === group.key;
                            return (
                                <div
                                    key={group.key}
                                    className={`hms-menubar-dropdown${active ? ' is-active' : ''}${isOpen ? ' is-open' : ''}`}
                                >
                                    <button
                                        ref={(el) => { triggerRefs.current[group.key] = el; }}
                                        type="button"
                                        className={`hms-menubar-trigger${active ? ' is-active' : ''}`}
                                        aria-expanded={isOpen}
                                        aria-haspopup="true"
                                        onClick={() => toggleGroup(group.key)}
                                    >
                                        {group.label}
                                        <ChevronDown size={14} className="hms-menubar-chevron" />
                                    </button>
                                </div>
                            );
                        })}

                        {loginRole?.roleType === 'SUPER_ADMIN' && (
                            <div
                                className={`hms-menubar-dropdown${
                                    isGroupActive(ONBOARDING_ITEMS.map((i) => i.link)) ? ' is-active' : ''
                                }${openKey === ONBOARDING_KEY ? ' is-open' : ''}`}
                            >
                                <button
                                    ref={(el) => { triggerRefs.current[ONBOARDING_KEY] = el; }}
                                    type="button"
                                    className={`hms-menubar-trigger${
                                        isGroupActive(ONBOARDING_ITEMS.map((i) => i.link)) ? ' is-active' : ''
                                    }`}
                                    aria-expanded={openKey === ONBOARDING_KEY}
                                    aria-haspopup="true"
                                    onClick={() => toggleGroup(ONBOARDING_KEY)}
                                >
                                    ON BOARDING
                                    <ChevronDown size={14} className="hms-menubar-chevron" />
                                </button>
                            </div>
                        )}
                    </nav>
                </div>
            </div>

            <MenuDropdownPanel
                items={openMenuItems || []}
                activeClassName={activeClassName}
                position={openMenuItems?.length ? menuPosition : null}
                onClose={closeMenu}
            />
        </>
    );
};

export default AppMenubar;
